import { formatNumber } from '@angular/common';
import { Component, effect, OnDestroy, OnInit } from '@angular/core';
import { RouterModule, RouterOutlet } from '@angular/router';
import { MenuItem } from 'primeng/api';
import { Menu } from 'primeng/menu';
import { Subscription } from 'rxjs';
import { NavbarComponent } from './navbar/navbar.component';
import { PermissionsService } from '../../shared/services/permissions.service';
import { SupportCacheService } from 'app/support/services/support-cache.service';
import { OfficesCacheService } from 'app/offices/services/offices-cache.service';
import { ProspectRemindersService } from 'app/invited/services/prospect-reminders.service';
import { ProspectReminderDialogComponent } from 'app/invited/components/prospect-reminder-dialog/prospect-reminder-dialog.component';

@Component({
  selector: 'app-protected-layout',
  imports: [RouterModule, RouterOutlet, NavbarComponent, Menu, ProspectReminderDialogComponent],
  templateUrl: './protected-layout.component.html',
  styleUrl: './protected-layout.component.scss',
  host: { class: 'flex flex-col h-screen' }
})
export class ProtectedLayoutComponent implements OnInit, OnDestroy {
  sidebarMenuItems: MenuItem[] = [];
  sidebarExpanded = false;

  private permissionsUpdatedSub!: Subscription;

  constructor(
    private readonly permissionsService: PermissionsService,
    private readonly supportCache: SupportCacheService,
    private readonly officesCache: OfficesCacheService,
    private readonly prospectReminders: ProspectRemindersService,
  ) {
    this.refreshSidebarMenuItems();

    effect(() => {
      this.supportCache.inboxUnreadCount();
      this.refreshSidebarMenuItems();
    });
  }

  /** Menu items the user is allowed to see (PrimeNG `p-menu`). */
  get menuModel(): MenuItem[] {
    return this.sidebarMenuItems.filter(item => item.visible !== false);
  }

  ngOnInit(): void {
    // Start polling inbox unread count if the user has the support permission
    if (this.permissionsService.isPermission('support')) {
      this.supportCache.startInboxPolling();
    }
    // Pre-warm + refresh the offices cache if the user can see the Accounts area
    this.syncOfficesCache();
    // Due-reminder checks live here so they only ever run while signed in: this layout
    // is not mounted on the login screen and is destroyed on sign-out.
    this.prospectReminders.startPolling();

    // Update the sidebar menu items when the permissions are updated
    this.permissionsUpdatedSub = this.permissionsService.permissionsUpdated$.subscribe(() => {
      this.refreshSidebarMenuItems();
      if (this.permissionsService.isPermission('support')) {
        this.supportCache.startInboxPolling();
      } else {
        this.supportCache.stopInboxPolling();
      }
      this.syncOfficesCache();
    });
  }

  ngOnDestroy(): void {
    this.permissionsUpdatedSub?.unsubscribe();
    this.supportCache.stopInboxPolling();
    this.officesCache.stopAutoRefresh();
    this.prospectReminders.stopPolling();
  }

  /** Start/stop the offices cache pre-warm + 10-min refresh based on the `accounts` permission. */
  private syncOfficesCache(): void {
    if (this.permissionsService.isPermission('accounts')) {
      this.officesCache.startAutoRefresh();
    } else {
      this.officesCache.stopAutoRefresh();
    }
  }

  private refreshSidebarMenuItems(): void {
    const items = [...this.permissionsService.menuItems];
    this.applySupportInboxUnreadBadge(items);
    this.sidebarMenuItems = items;
  }

  /** Sidebar Support item badge: inbox unread total from {@link SupportCacheService}. */
  private applySupportInboxUnreadBadge(items: MenuItem[]): void {
    const count = this.supportCache.inboxUnreadCount();
    const support = items.find(item => item.id === 'support');
    if (!support) return;
    support.badge = count > 0 ? formatNumber(count, 'en-US') : undefined;
    support.badgeStyleClass = count > 0 ? 'sidebar-nav-badge p-badge-sm p-badge-danger' : undefined;
  }
}
